import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import { ColorExtractor } from 'react-color-extractor'
import { addDecimalSeparator } from '../utils'
import Color from 'colorjs.io'

import { ShowcaseDisplayTypeID, Showcase, Offer, Link, LinksByOfferId, ShowcaseTypes } from '../types'
import { useGtmPush } from '../hooks/app.hook'

import basicClasses from './Basic.module.scss'
import RecommendedIcon from './images/recommendedIcon.svg'

const DEFAULT_ACCENT_COLOR = '#f2f2f2'

type TranslationFunc = ReturnType<typeof useTranslation>['t']

type ClassTable = Record<string, string>

export type ShowcaseTemplateProps = React.PropsWithChildren<{
  showcase: Showcase | null,
  offers: Offer[],
  linksByOfferId: LinksByOfferId,
  classes?: ClassTable,
}>

export type ShowcaseTemplateComponent = (props: ShowcaseTemplateProps) => JSX.Element

export default function Basic({ showcase, offers, linksByOfferId, classes = {} }: ShowcaseTemplateProps): JSX.Element {
  const { t } = useTranslation()
  const mergedClasses = Object.keys(classes).reduce((currentClasses, classId) => ({
    ...currentClasses,
    [classId]: classnames(currentClasses[classId], classes[classId]),
  }), basicClasses)

  const advantages = showcase?.advantages.filter(advantage => !!advantage) ?? []

  const [isCreditorInfoExpanded, setIsCreditorInfoExpanded] = useState<boolean>(false)

  const handleCreditorHeaderClick = useCallback(() => {
    setIsCreditorInfoExpanded(!isCreditorInfoExpanded)
  }, [isCreditorInfoExpanded, setIsCreditorInfoExpanded])

  return (
    <div className={mergedClasses.showcase}>
      {showcase?.logo?.[0] ?
        (
          <div className={mergedClasses.logoWrapper}>
            <div className={mergedClasses.logo}>
              <img className={mergedClasses.logoImage} src={showcase?.logo?.[0]?.source} alt={showcase?.title} />
            </div>
          </div>
        )
        :
        null
      }

      <div className={mergedClasses.headerWrapper}>
        <div className={mergedClasses.header}>
          <div className={mergedClasses.headerInnerWrapper}>
            <div className={mergedClasses.banner}>
              <div
                className={mergedClasses.bannerImage}
                style={showcase?.banner?.[0] ? { backgroundImage: `url(${showcase.banner[0].source})` } : undefined}
              ></div>
            </div>
            <h1 className={mergedClasses.headline}>
              {showcase?.title}
            </h1>
            {advantages.length ?
              (
                <ul className={mergedClasses.advantagesList}>
                  {advantages.map((advantage, index) => (
                    <li className={mergedClasses.advantage} key={index}>
                      {advantage}
                    </li>
                  ))}
                </ul>
              )
              :
              null
            }
          </div>
        </div>
      </div>

      {offers?.length ?
        (
          <>
            <div className={mergedClasses.banksListWrapper}>
              <div className={classnames(mergedClasses.banksList, {
                [mergedClasses.bankListCard]: showcase?.display_type_id === ShowcaseDisplayTypeID.Card,
                [mergedClasses.bankListTable]: showcase?.display_type_id === ShowcaseDisplayTypeID.Table,
              })}>
                {offers.map((offer, index) => (
                  <BankCard
                    showcase={showcase}
                    offer={offer}
                    link={linksByOfferId?.[offer.id] ?? null}
                    classes={mergedClasses}
                    key={offer.id}
                    index={index}
                  />
                ))}
              </div>
            </div>

            <div className={classnames(mergedClasses.creditorInfos, {
              [mergedClasses.creditorInfosExpanded]: isCreditorInfoExpanded,
            })}>
              <h2 className={mergedClasses.creditorInfoHeader} onClick={handleCreditorHeaderClick}>
                {t('creditorInfoHeader')}
                <svg className={mergedClasses.creditorInfoHeaderExpander} focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z" fill="currentColor"></path>
                </svg>
              </h2>
              <div className={mergedClasses.creditorInfosList}>
                {offers.map((offer, index) => (
                  <CreditorInfo offer={offer} classes={mergedClasses} key={index} />
                ))}
              </div>
            </div>
          </>
        )
        :
        null
      }

      <div className={mergedClasses.disclaimer}>
        {showcase?.disclaimer}
      </div>

      {showcase?.documentation?.[0] ?
        (
          <div className={mergedClasses.documentation}>
            <a href={showcase?.documentation[0].source} target="_blank" rel="noopener noreferrer">{t('documentationSource')}</a>
          </div>
        )
        :
        null
      }

      <CookieWarning classes={mergedClasses} />
    </div>
  )
}

type CustomBankField = {
  code: string,
  name: string,
  color?: string,
}

const CUSTOM_BANK_FIELDS = (t: TranslationFunc): CustomBankField[] => [
  {
    code: 'amount',
    name: t('amount'),
  },
  {
    code: 'credit_limit',
    name: t('credit_limit'),
  },
  {
    code: 'transfers_to_other_banks',
    name: t('transfers_to_other_banks'),
  },
  {
    code: 'cash_withdrawal',
    name: t('cash_withdrawal'),
    color: 'primary',
  },
  {
    code: 'tariff_cost',
    name: t('tariff_cost'),
  },
  {
    code: 'interest_rate',
    name: t('interest_rate'),
    color: 'primary',
  },
  {
    code: 'age',
    name: t('age'),
  },
  {
    code: 'annual_income',
    name: t('annual_income'),
  },
  {
    code: 'term',
    name: t('term'),
  },
  {
    code: 'maintenance',
    name: t('maintenance'),
  },
  {
    code: 'interest_free_period',
    name: t('interest_free_period'),
  },
  {
    code: 'down_payment',
    name: t('down_payment'),
  },
]

export type BankCardProps = React.PropsWithChildren<{
  showcase: Showcase | null,
  offer: Offer,
  link: Link | null,
  classes?: ClassTable,
  index: number
}>

export function BankCard({ showcase, offer, link, classes = basicClasses, index = 0 }: BankCardProps): JSX.Element {
  const { t } = useTranslation()
  const gtmPush = useGtmPush()
  const [dominantColors, setDominantColors] = useState<string[]>([])
  const isRecommended =
    showcase?.type === ShowcaseTypes.universal
      ? index < showcase?.recommended_offers_count
      : showcase?.recommended_offers?.includes(Number(offer.id)) ?? false

  const logoSrc = offer.icons.favicon_svg?.path ?? offer.icons.favicon_orig?.path ?? offer.favicon?.[0]?.source
  const iconSrc = offer.favicon?.[0]?.source ?? offer.icons.favicon_svg?.path ?? offer.icons.favicon_orig?.path ?? ''

  const handleClick = useCallback(() => {
    gtmPush({
      'event': 'redirect',
      'event_name': 'redirect',
      'offer_id': offer.id,
      'offer_name': offer.name
    })
  }, [offer.id, offer.name, gtmPush])

  const handleColorsExtract = useCallback((colors: string[]) => {
    setDominantColors(colors)
  }, [setDominantColors])

  const accentColor = new Color(dominantColors[0] ?? DEFAULT_ACCENT_COLOR)
  accentColor.lighten(.25)
  const backColor = accentColor.toString({ format: 'hex' })

  return (
    <div style={{ '--back-color': backColor === '#fff' ? DEFAULT_ACCENT_COLOR : backColor }} className={classnames(classes.bankWrapper, { [classes.recommendedBankWrapper]: isRecommended })}>
      {isRecommended ?
        (
          <div className={classes.bankRecommendedLabel}>
            <img src={RecommendedIcon} alt="" />
            {showcase?.recommended_label}
          </div>
        )
        :
        null
      }
      <div className={classes.recommendedBankLogoImageWrapper}>
        {isRecommended && (iconSrc || logoSrc) ?
          (
            <>
              <ColorExtractor
                src={`/api/icon/?url=${encodeURIComponent(iconSrc)}`}
                getColors={handleColorsExtract}
                onError={(err: any) => console.log('err', err)}
              />
              <img className={classes.bankIconImage} src={iconSrc} alt={offer.name} />
              <img className={classes.bankLogoImage} src={logoSrc} alt={offer.name} />
            </>
          ) : null
        }
      </div>
      <div className={classnames(classes.bank, { [classes.recommendedBank]: isRecommended })}>
        <div className={classes.bankName}>
          <div className={classes.bankLogo}>
            {iconSrc && !isRecommended ?
              (
                <div className={classes.bankLogoImageWrapper}>
                  <img className={classes.bankIconImage} src={iconSrc} alt={offer.name} />
                  <img className={classes.bankLogoImage} src={logoSrc} alt={offer.name} />
                </div>
              )
              :
              null
            }
            <span>{offer.custom_fields?.showcase_name ?? offer.name}</span>
          </div>
        </div>

        <div className={classes.bankPropsList}>
          {CUSTOM_BANK_FIELDS(t).map(({ code, name, color = null }) => {
            const displayValue = getCustomFieldValue(offer, code, t)

            return displayValue ?
              (
                <div key={code} className={classes.bankProp}>
                  <span className={classes.bankPropName}>
                    {name}
                  </span>
                  {' '}
                  <span className={classnames(classes.bankPropValue, { [classes.bankPropValuePrimary]: color === 'primary' })}>
                    {displayValue}
                  </span>
                </div>
              )
              :
              null
          })}
        </div>

        {(showcase && link) ?
          (
            <div className={classes.bankButtonsList}>
              <OfferLink showcase={showcase} link={link} className={classes.button} onClick={handleClick} target="_blank" rel="noopener noreferrer">{t('bankButton')}</OfferLink>

              {offer.custom_fields.ogrn && (
                <div className={classes.bankOgrnNote}>
                  {t('advertising')} {offer.custom_fields.legal_entity_name}
                  <div>{t('ogrn')}: {offer.custom_fields.ogrn}</div>
                </div>
              )}
            </div>
          )
          :
          null
        }
      </div>
    </div>
  )
}

export type OfferLinkProps = Omit<React.HTMLProps<HTMLAnchorElement>, 'href'> & {
  showcase: Showcase,
  link: Link,
}

export function OfferLink({ showcase, link, children, ...rest }: OfferLinkProps): JSX.Element {
  const url = link.url

  const queryParams = {
    erid: link.ord_token || '',
    sa: showcase.subaccount || '',
    sa2: showcase.subaccount2 || '',
    sa3: showcase.subaccount3 || '',
    sa4: showcase.subaccount4 || '',
    sa5: showcase.subaccount5 || '',
  }

  const qs = queryString.stringify(queryParams, { skipEmptyString: true })

  const href = qs ? url + (url.indexOf('?') >= 0 ? '&' : '?') + qs : url

  return (
    <a {...rest} href={href}>{children}</a>
  )
}

const CUSTOM_CREDITOR_FIELDS = (t: TranslationFunc) => [
  {
    code: 'legal_entity_name',
    name: t('legal_entity_name'),
  },
  {
    code: 'license_number',
    name: t('license_number'),
  },
  {
    code: 'inn',
    name: t('inn'),
  },
  {
    code: 'ogrn',
    name: t('ogrn'),
  },
  {
    code: 'legal_address',
    name: t('legal_address'),
  },
  {
    code: 'total_loan_cost',
    name: t('total_loan_cost'),
  },
  {
    code: 'max_loan_term',
    name: t('max_loan_term'),
  },
]

export type CreditorInfoProps = React.PropsWithChildren<{
  offer: Offer,
  classes?: ClassTable,
}>

export function CreditorInfo({ offer, classes = basicClasses }: CreditorInfoProps): JSX.Element {
  const { t } = useTranslation()
  return (
    <div className={classes.creditorInfo}>
      <h3 className={classes.creditorHeader}>
        {offer.name}
      </h3>
      {CUSTOM_CREDITOR_FIELDS(t).map(({ code, name }) => {
        const displayValue = getCustomFieldValue(offer, code, t)

        return displayValue ?
          (
            <div key={code} className={classes.creditorProp}>
              <span className={classes.creditorPropName}>
                {name}:
              </span>
              {' '}
              <span className={classes.creditorPropValue}>
                {displayValue}
              </span>
            </div>
          )
          :
          null
      })}
    </div>
  )
}

const CUSTOM_FIELD_UNITS: Record<string, string> = {
  'age': 'years',
  'tariff_cost': 'rouble_per_month',
}

const CUSTOM_FIELDS_PERCENTS = [
  'total_loan_cost',
  'interest_rate',
  'annual_income',
  'down_payment',
]

const CUSTOM_FIELDS_PRECISION: Record<string, number> = {
  total_loan_cost: 3
}

type FieldSuffixTranslationFunc = (count: number, t: TranslationFunc, isNominal: boolean) => string
type FieldSuffix = string | string[] | FieldSuffixTranslationFunc

const CUSTOM_FIELD_SUFFIX_MAP: Record<string, FieldSuffix> = {
  rouble: '₽',
  dollar: '$',
  euro: '€',
  days: (count, t, isNominal = true) => t(isNominal ? 'days' : 'of_days', { count }),
  weeks: (count, t, isNominal = true) => t(isNominal ? 'weeks' : 'of_weeks', { count }),
  months: (count, t, isNominal = true) => t(isNominal ? 'months' : 'of_months', { count }),
  years: (count, t, isNominal = true) => t(isNominal ? 'years' : 'of_years', { count }),
  rouble_per_month: (count, t, isNominal = true) => t('rub_per_month'),
}

function getCountedSuffix(count: number, suffix: FieldSuffix | null, isNominal: boolean = true, t: TranslationFunc): string {
  if (!suffix) {
    return ''
  }

  if (typeof suffix === 'function') {
    return suffix(count, t, isNominal)
  }

  if (typeof suffix === 'string') {
    return suffix
  }

  if (count % 100 < 10 || count % 100 > 20) {
    if (count % 10 === 1) {
      return isNominal ? suffix[0] : suffix[1]
    }

    if (count % 10 === 2 || count % 10 === 2 || count % 10 === 3) {
      return suffix[1]
    }
  }

  if (count !== Math.floor(count)) {
    return suffix[1]
  }

  return suffix[2]
}

export function getCustomFieldValue(offer: Offer, propName: string, t: TranslationFunc): JSX.Element | null {
  const fieldCurrency = offer.custom_fields?.[`${propName}_currency`]
  const fieldUnit = offer.custom_fields?.[`${propName}_unit`] ?? CUSTOM_FIELD_UNITS[propName]
  const fieldPrecision = CUSTOM_FIELDS_PRECISION[propName] ?? null

  const fieldSuffix = CUSTOM_FIELDS_PERCENTS.includes(propName) ?
    '%' :
    fieldCurrency ?
      fieldCurrency as string :
      fieldUnit ? CUSTOM_FIELD_SUFFIX_MAP[fieldUnit as string] : null

  const fieldValue = offer.custom_fields?.[propName]

  if (fieldValue !== null && fieldValue !== undefined) {
    return (
      <>
        {addDecimalSeparator(fieldValue, fieldPrecision)}&nbsp;{getCountedSuffix(Number(fieldValue), fieldSuffix, true, t)}
      </>
    )
  }

  const fieldValueFrom = offer.custom_fields?.[`${propName}_from`]
  const fieldValueTo = offer.custom_fields?.[`${propName}_to`]

  if (fieldValueFrom !== null && fieldValueFrom !== undefined && fieldValueTo !== null && fieldValueTo !== undefined) {
    if (fieldValueFrom === fieldValueTo) {
      return (
        <>
          {addDecimalSeparator(fieldValueFrom, fieldPrecision)}&nbsp;{getCountedSuffix(Number(fieldValueFrom), fieldSuffix, true, t)}
        </>
      )
    }
    return (
      <>
        {t('rangeFrom')}&nbsp;{addDecimalSeparator(fieldValueFrom, fieldPrecision)} {t('rangeTo')}&nbsp;{addDecimalSeparator(fieldValueTo, fieldPrecision)}&nbsp;{getCountedSuffix(Number(fieldValueTo), fieldSuffix, false, t)}
      </>
    )
  }

  if (fieldValueFrom !== null && fieldValueFrom !== undefined) {
    return (
      <>
        {t('rangeFrom')}&nbsp;{addDecimalSeparator(fieldValueFrom, fieldPrecision)}&nbsp;{getCountedSuffix(Number(fieldValueFrom), fieldSuffix, false, t)}
      </>
    )
  }

  if (fieldValueTo !== null && fieldValueTo !== undefined) {
    return (
      <>
        {t('rangeTo')}&nbsp;{addDecimalSeparator(fieldValueTo, fieldPrecision)}&nbsp;{getCountedSuffix(Number(fieldValueTo), fieldSuffix, false, t)}
      </>
    )
  }

  return null
}

export type CookieWarningProps = React.PropsWithChildren<{
  classes?: ClassTable,
}>

const COOKIE_ACCEPTED = 'yes'

export function CookieWarning({ classes = basicClasses }: CookieWarningProps): JSX.Element | null {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(Cookies.get('cookie-accepted') !== COOKIE_ACCEPTED)

  const handleClose = useCallback(() => {
    Cookies.set('cookie-accepted', COOKIE_ACCEPTED, { expires: 30 })
    setOpen(false)
  }, [setOpen])

  if (!open) {
    return null
  }

  return (
    <div className={classes.cookieWarning}>
      {t('cookieWarning')}

      <div className={classes.cookieWarningButtons}>
        <button type="button" className={classes.button} onClick={handleClose}>{t('cookieAcceptButton')}</button>
      </div>
    </div>
  )
}
